import type { ListMethod } from '@/types/list-method';

import type { FileImportPayload, ProfileSelection } from './list-save.model';
import { mergeSelection } from './merge-selection';

/** A normalised, declarative description of what a save saga must execute. */
export interface SavePlan {
  /** File import step, or null to skip it. */
  fileImport: FileImportPayload | null;
  /** Profile IDs to associate; empty to skip the association step. */
  finalIds: string[];
  /** Profile IDs to enrich; empty to skip queuing an enrichment session. */
  enrichTargetIds: string[];
  /** Whether the profiles the file import creates join the enrichment session. */
  enrichFileCreated: boolean;
}

/** Minimal draft shape this needs; both Add's `ListDraft` and Edit's `EditDraft` satisfy it. */
type DraftWithMethods = { methods: ListMethod[] };

/**
 * Turn a draft + the UI-computed selection into an ordered, normalised plan the
 * saga can execute step-by-step. Pure and side-effect-free: every branch of the
 * save flow (empty list, import-only, enrich toggles) is decidable and testable
 * here rather than inside the saga.
 *
 * This is also the **last line of defence** on what reaches the server. Each
 * profile source belongs to a method, and a source whose method is not selected
 * is dropped here whatever the UI hands over; the wizard used to ship an
 * unselected method's leftovers and silently inflate the list
 * (docs/product-issues.md PI-40).
 *
 * An "empty list" (the exclusive `empty` method; Add only) carries no profile
 * work, so the plan strips everything but the list write itself.
 */
export function buildSavePlan(draft: DraftWithMethods, selection: ProfileSelection): SavePlan {
  if (draft.methods.includes('empty')) {
    return { fileImport: null, finalIds: [], enrichTargetIds: [], enrichFileCreated: false };
  }

  const has = (method: ListMethod) => draft.methods.includes(method);
  const { finalIds } = mergeSelection(
    has('select') ? selection.filterIds : [],
    has('import') ? selection.importedIds : [],
  );
  const fileImport = has('file') ? selection.fileImport : null;

  return {
    fileImport,
    finalIds,
    // Enrichment targets are segmented from the union of all three sources, so
    // they only mean something when the plan actually saves profiles.
    enrichTargetIds: finalIds.length > 0 || fileImport ? selection.enrichTargetIds : [],
    // A file holding full profile data marks what it creates as enriched today:
    // enriching them again would only spend credits.
    enrichFileCreated:
      fileImport !== null && !fileImport.isEnriched && selection.enrichFileCreated === true,
  };
}
